import { createFileRoute } from "@tanstack/react-router";
import Left from "@repo/icons/default/Left";
import { graphql } from "@repo/graphql-types/gql";
import {
  HeaderRoot,
  HeaderButton,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { ensureQueryData, useGraphQL } from "@/hooks/use-graphql";
import { getIP } from "@/lib/ip-getter";
import { ReservedScheduleForm } from "@/components/reserved-schedules/reserved-schedule-form";
import { HasUserAcceptedTermsOfUseQuery } from "@/components/reserved-schedules/reserved-schedule-terms-of-use-drawer";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { resolveReservedScheduleModality } from "@/lib/reserved-schedules/resolve-reserved-schedule-modality";

import type { JSX } from "react";

const GetLocationsQuery = graphql(/* GraphQL */ `
  query GetLocationsQuery {
    ...GetLocationsFragment
    ...ReservedScheduleModalitiesFragment
  }
`);

const CreateReservedSchedulePage = (): JSX.Element => {
  const queryResult = useGraphQL(GetLocationsQuery);

  const { data } = queryResult;
  const routerContext = Route.useLoaderData();
  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Criar período garantido" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <ReservedScheduleForm
              locationsFragment={data}
              modalitiesFragment={data}
              accessibleModalities={routerContext.accessibleModalities}
              defaultModality={routerContext.defaultModality}
              acceptedTermsOfUseTypes={routerContext.acceptedTermsOfUseTypes}
              memberIP={routerContext.memberIP}
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/reserved-schedules/create/")({
  component: CreateReservedSchedulePage,
  loader: async ({ context }) => {
    const modality = resolveReservedScheduleModality(context);

    // Carrega os termos já aceitos, mas **não gateia aqui**: o termo é função da categoria
    // escolhida, e no loader a escolha ainda não existe. Gatear pela pré-seleção deixava quem
    // aceitou só o termo da parceria escolher a convencional e criar um PG cujo termo nunca
    // assinou. Quem pede o aceite é `ReservedScheduleTermsOfUseDrawer`, no submit.
    const data = await ensureQueryData(context, HasUserAcceptedTermsOfUseQuery);

    const memberIP = await getIP();
    return {
      ...modality,
      memberIP,
      acceptedTermsOfUseTypes: data.tbUsuariosTermosDeUso.map(
        (termsOfUse) => termsOfUse.codTipoTermoDeUso,
      ),
    };
  },
});
